import { CloseOutlined, RightOutlined } from '@ant-design/icons'
import { Empty, Flex, message } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import {
  cancelVacation,
  getMyVacationHistory,
  getMyVacationInfo,
  getVacationSettlementDetail,
} from '../../api/aops.vacation.api'
import AopsSimpleHeader from '../../components/apos/AopsSimpleHeader'
import SectionHeader from '../../components/apos/SectionHeader'
import AopsVacationModal from '../../components/apos/vacation/AopsVacationModal'
import { userState } from '../../store/user'
import { formatLeave, returnMonthToYear } from '../../utils/calcuateAnnualLeave'
import './vacationPage.scss'
const VacationPage = () => {
  const [user] = useRecoilState(userState)
  const { userId: paramUserId } = useParams()
  const requestUserId = paramUserId || user?.id
  const [myVacationInfo, setMyVacationInfo] = useState(null)
  const [myVacationHistory, setMyVacationHistory] = useState(null)
  const [isVacationModalOpen, setIsVacationModalOpen] = useState(false)
  const [openSettlementTooltip, setOpenSettlementTooltip] = useState(false)
  const [settlementDetail, setSettlementDetail] = useState(null)
  const returnStatusBadge = (eventType, status) => {
    // MONTHLY_CREATE, CREATED → 발생
    if (eventType === 'MONTHLY_CREATE' || eventType === 'CREATED') {
      return { text: '발생', className: 'status-occurred' }
    }

    // USE → status에 따라 분기
    if (eventType === 'USE') {
      switch (status) {
        case 'PENDING':
          return { text: '대기', className: 'status-pending' }
        case 'APPROVED':
          return { text: '승인', className: 'status-approved' }
        case 'REJECTED':
          return { text: '거절', className: 'status-rejected' }
        case 'CANCELED':
          return { text: '취소', className: 'status-rejected' }
        default:
          return { text: '대기', className: 'status-pending' }
      }
    }

    // EXPIRED → 만료
    if (eventType === 'EXPIRED') {
      return { text: '만료', className: 'status-expired' }
    }

    return { text: '-', className: 'status-pending' }
  }

  const getTimeSlotText = (timeSlot) => {
    switch (timeSlot) {
      case 'ALL':
        return '종일'
      case 'AM':
        return '오전'
      case 'PM':
        return '오후'
      default:
        return '-'
    }
  }

  const getEventTypeText = (eventType) => {
    switch (eventType) {
      case 'MONTHLY_CREATE':
        return '월차 발생'
      case 'CREATED':
        return '연차 발생'
      case 'USE':
        return '연차 사용'
      case 'EXPIRED':
        return '연차 소멸'
      default:
        return '-'
    }
  }

  const getPayoutStatusText = (payoutStatus) => {
    switch (payoutStatus) {
      case 'PENDING':
        return '대기'
      case 'COMPLETED':
        return '지급 완료'
      default:
        return '-'
    }
  }

  const refreshVacationData = async () => {
    await fetchMyVacationInfo()
    await fetchMyVacationHistory()
  }

  const fetchMyVacationInfo = async () => {
    try {
      const res = await getMyVacationInfo(requestUserId)
      setMyVacationInfo(res)
      // console.log('myVacationInfo::', res)
    } catch (error) {
      console.error('getMyVacationInfo error::', error)
      setMyVacationInfo(null)
      return false
    }
  }
  const fetchMyVacationHistory = async () => {
    if (!requestUserId) return
    try {
      const res = await getMyVacationHistory(requestUserId)
      // console.log('myVacationHistory response::', res)
      // API가 response.data를 반환하므로, res가 이미 data일 수도 있고 res.data일 수도 있음
      const historyData = Array.isArray(res) ? res : res?.data || res || []
      setMyVacationHistory(historyData)
      // console.log('myVacationHistory parsed::', historyData)
    } catch (error) {
      console.error('getMyVacationHistory error::', error)
      setMyVacationHistory([])
      return false
    }
  }

  useEffect(() => {
    if (requestUserId) {
      refreshVacationData()
    }
  }, [requestUserId])

  const fetchVacationSettlementDetail = async (settlementId) => {
    setOpenSettlementTooltip(true)
    try {
      const res = await getVacationSettlementDetail(settlementId)
      console.log('vacationSettlementDetail response::', res)
      setSettlementDetail(res)
    } catch (error) {
      console.error('getVacationSettlementDetail error::', error)
    }
  }
  useEffect(() => {
    setOpenSettlementTooltip(false)
  }, [])

  // 연차 취소
  const handleCancelVacation = async (historyId, userId) => {
    Swal.fire({
      title: '연차 취소',
      text: '연차 취소하시겠습니까?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '취소하기',
      cancelButtonText: '예',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await cancelVacation(historyId, userId)
          console.log('cancelVacation response::', res)
          message.success('연차 취소가 완료되었습니다.')
          refreshVacationData()
        } catch (error) {
          console.error('cancelVacation error::', error)
          message.error('연차 취소에 실패했습니다.')
          return false
        }
      }
    })
  }

  return (
    <div className="vacation-page">
      <AopsSimpleHeader title="연차 관리" />

      <div className="my-hr-content-container">
        {/* 연차 현황 영역 */}
        <SectionHeader
          title="연차 현황"
          subtitle="내 연차를 확인하고 휴가를 신청해보세요"
          buttonText="연차 신청하기"
          onButtonClick={() => setIsVacationModalOpen(true)}
          iconType="arrow-right"
        />
        <section className="vacation-box">
          <div className="vacation-details-header">내 연차 내역</div>
          <div className="vacation-details-content">
            <div className="employee-info">
              <div className="employee-name">{myVacationInfo?.userName}</div>
              <div className="employee-department">
                {myVacationInfo?.studioName || '--'} 스튜디오 |{' '}
                {myVacationInfo?.teamName || '--'}
              </div>
              <div className="employee-tag">
                {returnMonthToYear(Number(myVacationInfo?.tenureYear || 0))} ·
                {dayjs(myVacationInfo?.hiredDate).format('YY년 MM/DD')} 입사
              </div>
            </div>
            <div className="vacation-metrics">
              <div className="metric-item">
                <div className="metric-label">총 발생 연차</div>
                <div className="metric-value">
                  {formatLeave(myVacationInfo?.totalDays) || 0}
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-label">사용 연차 (승인)</div>
                <div className="metric-value">
                  {formatLeave(myVacationInfo?.usedDays) || '-'}
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-label">잔여 연차</div>
                <div className="metric-value">
                  {formatLeave(myVacationInfo?.remainedDays) || 0}
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-label">연차 갱신일</div>
                <div className="metric-value">
                  {dayjs(myVacationInfo?.validStartDate).format('YY.MM.DD') ||
                    '--'}
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-label">연차 만료일</div>
                <div className="metric-value">
                  {dayjs(myVacationInfo?.validEndDate).format('YY.MM.DD') ||
                    '--'}
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="vacation-box">
          <div className="vacation-history-header">사용 연차 이력 확인</div>
          <div className="table-wrapper">
            <table className="vacation-history-table">
              <thead className="detail-list-thead">
                <tr>
                  <th>상태</th>
                  <th>종류</th>
                  <th>유형</th>
                  <th>일자</th>
                  <th>증감</th>
                  <th>처리</th>
                </tr>
              </thead>
              <tbody>
                {myVacationHistory && myVacationHistory.length > 0 ? (
                  myVacationHistory.map((item) => {
                    const statusBadge = returnStatusBadge(
                      item?.eventType,
                      item?.status,
                    )
                    const amoutIsPlus = item?.amount > 0
                    const amount = formatLeave(Math.abs(item?.amount || 0))
                    return (
                      <tr
                        key={`${item.id}-${item.targetStartDate}-my-vacation-history`}
                      >
                        <td>
                          <span
                            className={`status-badge ${statusBadge.className}`}
                          >
                            {statusBadge.text}
                          </span>
                        </td>
                        <td>{getEventTypeText(item?.eventType)}</td>
                        <td>
                          {item?.eventType === 'USE'
                            ? getTimeSlotText(item?.timeSlot)
                            : ''}
                        </td>
                        <td>
                          {dayjs(item?.targetStartDate).format('YY.MM.DD')}
                        </td>
                        <td>{amoutIsPlus ? `+${amount}` : `-${amount}`}</td>
                        <td style={{ position: 'relative' }}>
                          {item?.eventType === 'USE' &&
                          item?.status === 'PENDING' ? (
                            <button
                              className="action-button"
                              onClick={() =>
                                handleCancelVacation(item?.id, item?.userId)
                              }
                            >
                              취소하기
                              <RightOutlined className="action-button-icon" />
                            </button>
                          ) : item?.eventType === 'EXPIRED' ? (
                            <>
                              <button
                                className="action-button"
                                onClick={() =>
                                  fetchVacationSettlementDetail(
                                    item?.settlementId,
                                  )
                                }
                              >
                                연차 보상 확인{' '}
                                <RightOutlined className="action-button-icon" />
                              </button>
                              {openSettlementTooltip && (
                                <div
                                  className="action-button-tooltip"
                                  onClick={() =>
                                    setOpenSettlementTooltip(false)
                                  }
                                >
                                  <button className="x-close-icon-button">
                                    <CloseOutlined className="close-tooltip-icon" />
                                  </button>
                                  <p className="date-text">
                                    {dayjs(settlementDetail?.expiryDate)
                                      .subtract(1, 'year')
                                      .add(1, 'day')
                                      .format('YY.MM.DD')}{' '}
                                    <span>(입사년도 기준)</span>
                                  </p>
                                  <Flex gap={14} style={{ marginTop: `8px` }}>
                                    <div className="action-button-tooltip-item">
                                      <p>
                                        연차 보상 확인 |{' '}
                                        <span>
                                          {settlementDetail?.unusedDays || 0}일
                                        </span>
                                      </p>
                                    </div>
                                    <div className="action-button-tooltip-item">
                                      <p>
                                        연차 보상 |{' '}
                                        <span>
                                          {getPayoutStatusText(
                                            settlementDetail?.payoutStatus,
                                          )}
                                        </span>
                                      </p>
                                    </div>
                                  </Flex>
                                </div>
                              )}
                            </>
                          ) : (
                            ''
                          )}
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: 'center', padding: '40px' }}
                    >
                      <Empty description="연차 이력이 없습니다." />
                      {/* 연차 이력이 없습니다. */}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <AopsVacationModal
        open={isVacationModalOpen}
        onClose={() => setIsVacationModalOpen(false)}
        title="연차 신청하기"
        onRefresh={refreshVacationData}
      />
    </div>
  )
}

export default VacationPage
